import { useBatchTransitions } from '../BatchCheckpointsScanner/useBatchTransitions';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { Option } from '@/components/inputs/AutocompleteInput';
import { validateBulkEntries } from './validateBulkEntries';
import { useState, useMemo, useCallback } from 'react';
import { parseBulkCodes } from './parseBulkCodes';
import { MAX_SHIPPINGS_PER_BATCH } from '../../config/batchLimits';
import { useNotification } from '@/hooks';
import {
  useValidateShippingBatchMutation,
  useExecuteBatchTransitionBulkMutation,
} from '../../services/batchCheckpoints.endpoints';

type BulkUploadPhase = 'input' | 'processing' | 'failed';
type ProcessingStage = 'validating' | 'executing' | null;
type UseBatchTransitionsQueryHook = Parameters<typeof useBatchTransitions>[1];

interface UseBulkCheckpointUploadOptions {
  useTransitionsQuery?: UseBatchTransitionsQueryHook;
}

export const useBulkCheckpointUpload = (
  { useTransitionsQuery }: UseBulkCheckpointUploadOptions = {}
) => {
  const { showNotification } = useNotification();

  const [isOpen, setIsOpen] = useState(false);
  const [transition, setTransition] = useState<Option | null>(null);
  const [rawText, setRawText] = useState('');
  const [phase, setPhase] = useState<BulkUploadPhase>('input');
  const [processingStage, setProcessingStage] = useState<ProcessingStage>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [failures, setFailures] = useState<
    { shippingNumber: string; reason: string; isLinkable: boolean }[]
  >([]);
  const [resultMessage, setResultMessage] = useState('');

  const selectedTransitionId = transition?.data as string | undefined;
  const { transitionOptions, isTransitionsLoading, logisticOperatorId } =
    useBatchTransitions(selectedTransitionId, useTransitionsQuery);

  const [validateShippingBatch] = useValidateShippingBatchMutation();
  const [executeBatch] = useExecuteBatchTransitionBulkMutation();

  const parsed = useMemo(() => parseBulkCodes(rawText), [rawText]);

  const resetState = useCallback(() => {
    setTransition(null);
    setRawText('');
    setPhase('input');
    setProcessingStage(null);
    setProgress({ done: 0, total: 0 });
    setFailures([]);
    setResultMessage('');
  }, []);

  const openModal = useCallback(() => {
    resetState();
    setIsOpen(true);
  }, [resetState]);

  const closeModal = useCallback(() => {
    if (phase === 'processing') return;
    setIsOpen(false);
    resetState();
  }, [phase, resetState]);

  const onConfirm = useCallback(async () => {
    if (
      !transition ||
      parsed.validEntries.length === 0 ||
      parsed.validEntries.length > MAX_SHIPPINGS_PER_BATCH
    ) {
      return;
    }

    const transitionId = transition.data as string;

    setPhase('processing');
    setProcessingStage('validating');
    setProgress({ done: 0, total: 0 });

    const validationResults = await validateBulkEntries(
      parsed.validEntries,
      (codes) =>
        validateShippingBatch({
          transitionId,
          codes,
          mode: 'COMMERCIAL_NUMBER',
          logisticOperatorId,
        }).unwrap(),
      (done, total) => setProgress({ done, total })
    );

    const resolved = validationResults.filter((r) => r.success);
    const validationFailures = validationResults
      .filter((r) => !r.success)
      .map((r) => ({ shippingNumber: r.code, reason: r.reason!, isLinkable: false }));

    if (resolved.length === 0) {
      setFailures(validationFailures);
      setResultMessage('Ningún pedido pudo validarse para esta transición.');
      setPhase('failed');
      return;
    }

    setProcessingStage('executing');

    try {
      const result = await executeBatch({
        transitionId,
        codes: resolved.map((r) => r.number!),
      }).unwrap();

      const allFailures = [
        ...validationFailures,
        ...result.failed.map((f) => ({ shippingNumber: f.code, reason: f.reason, isLinkable: true })),
      ];

      if (allFailures.length === 0) {
        showNotification(result.message, NotificationSeverity.success);
        setIsOpen(false);
        resetState();
        return;
      }

      const successCount = result.successful.length;
      setFailures(allFailures);
      setResultMessage(
        `${successCount} de ${parsed.validEntries.length} pedidos actualizados correctamente.`
      );
      setPhase('failed');
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      const message = error.data?.message || error.message || 'Error al aplicar los cambios de estado';
      showNotification(message, NotificationSeverity.error);
      setPhase('input');
      setProcessingStage(null);
    }
  }, [
    transition,
    parsed.validEntries,
    validateShippingBatch,
    executeBatch,
    logisticOperatorId,
    showNotification,
    resetState,
  ]);

  const onCloseFailedResult = useCallback(() => {
    setIsOpen(false);
    resetState();
  }, [resetState]);

  const canSubmit =
    !!transition &&
    parsed.validEntries.length > 0 &&
    parsed.validEntries.length <= MAX_SHIPPINGS_PER_BATCH &&
    parsed.unrecognizedLines.length === 0 &&
    phase === 'input';

  return {
    isOpen,
    openModal,
    closeModal,
    transition,
    setTransition,
    transitionOptions,
    isTransitionsLoading,
    rawText,
    setRawText,
    parseStats: parsed.stats,
    unrecognizedLines: parsed.unrecognizedLines,
    phase,
    processingStage,
    progress,
    failures,
    resultMessage,
    canSubmit,
    onConfirm,
    onCloseFailedResult,
  };
};
